import { getEventType } from '../constants/eventTypes';

const asOptionList = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item || '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
  return [];
};

export const selectedPollOptions = (participant, eventInfo) => {
  const fromField = asOptionList(participant?.selectedPollOptions);
  if (fromField.length) return fromField;
  const details = participant?.responseDetails || {};
  const fromDetails = asOptionList(details.selectedOptions || details.pollOptions);
  if (fromDetails.length) return fromDetails;
  const userId = participant?.userId;
  if (userId && eventInfo?.pollVotesByUserId) {
    return asOptionList(eventInfo.pollVotesByUserId[userId]);
  }
  return [];
};

export const isPollEvent = (eventOrType) => {
  if (!eventOrType) return false;
  if (typeof eventOrType === 'string') return String(eventOrType).toUpperCase() === 'POLL';
  const type = String(eventOrType.eventType || '').toUpperCase();
  const responseType = String(
    eventOrType.responseType
    || eventOrType.responsePolicy?.type
    || '',
  ).toUpperCase();
  return type === 'POLL' || responseType === 'POLL' || responseType === 'MULTIPLE_CHOICE';
};

export const resolvePollConfig = (info = {}) => {
  const policy = info.responsePolicy || {};
  const cfg = info.configuration || {};
  const question = String(
    info.pollQuestion
    || policy.pollQuestion
    || cfg.question
    || info.name
    || '',
  ).trim();
  const raw = info.pollOptions || policy.pollOptions || cfg.options || [];
  let options = (Array.isArray(raw) ? raw : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  if (!options.length) {
    const derived = new Set();
    (info.participants || []).forEach((participant) => {
      selectedPollOptions(participant, info).forEach((option) => derived.add(option));
    });
    Object.values(info.pollVotesByUserId || {}).forEach((value) => {
      asOptionList(value).forEach((option) => derived.add(option));
    });
    options = Array.from(derived);
  }
  const allowMultiple = Boolean(
    info.allowMultiple
    ?? policy.allowMultiple
    ?? cfg.allowMultiple,
  );
  return { question, options, allowMultiple };
};

export const tallyPollVotes = (options, participants = [], eventInfo) => {
  const counts = {};
  const voters = {};
  const optionList = options.length
    ? options
    : Array.from(new Set((participants || []).flatMap((p) => selectedPollOptions(p, eventInfo))));
  optionList.forEach((option) => {
    counts[option] = 0;
    voters[option] = [];
  });
  let voterCount = 0;
  const seen = new Set();
  const addVote = (participant, selected) => {
    const matched = selected.filter((option) => counts[option] != null);
    if (!matched.length) return;
    const key = participant?.userId || `anon-${voterCount}`;
    if (seen.has(key)) return;
    seen.add(key);
    voterCount += 1;
    matched.forEach((option) => {
      counts[option] += 1;
      voters[option].push(participant || { userId: key });
    });
  };
  (participants || []).forEach((participant) => {
    addVote(participant, selectedPollOptions(participant, eventInfo));
  });
  Object.entries(eventInfo?.pollVotesByUserId || {}).forEach(([userId, raw]) => {
    const existing = (participants || []).find((p) => String(p.userId) === String(userId));
    addVote(existing || { userId }, asOptionList(raw));
  });
  return { counts, voters, voterCount };
};

export const pollPercent = (count, voterCount) => {
  if (!voterCount) return 0;
  return Math.round((Number(count || 0) / voterCount) * 100);
};

export const pollDisplayName = (event) => String(
  event?.name
  || event?.pollQuestion
  || event?.responsePolicy?.pollQuestion
  || '',
).trim();

export const followUpEventName = (pollName, eventTypeId) => {
  const label = getEventType(eventTypeId)?.label || eventTypeId || 'Event';
  const base = String(pollName || '').trim();
  return base ? `${base} - ${label}` : label;
};

export const followUpParticipantsFromPoll = (event, mode, creatorId) => {
  const list = Array.isArray(event?.participants) ? event.participants : [];
  const voted = (participant) => selectedPollOptions(participant, event).length > 0;
  let chosen = list;
  if (mode === 'RESPONDED') chosen = list.filter(voted);
  if (mode === 'NOT_RESPONDED') chosen = list.filter((participant) => !voted(participant));
  const byId = new Map();
  chosen.forEach((participant) => {
    if (participant?.userId) byId.set(String(participant.userId), participant);
  });
  if (creatorId && !byId.has(String(creatorId))) {
    const creator = list.find((participant) => String(participant.userId) === String(creatorId));
    byId.set(String(creatorId), creator || { userId: creatorId, role: 'REQUIRED', status: 'ACCEPTED' });
  }
  return Array.from(byId.values()).map((participant) => ({
    userId: participant.userId,
    role: 'REQUIRED',
    status: String(participant.userId) === String(creatorId) ? 'ACCEPTED' : 'INVITED',
  }));
};
